import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Link,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { tenantAPI } from "../../services/api/tenant";
import { propertyAPI } from "../../services/api/property";
import { artisanAPI } from "../../services/api/artisan";
import { tasksAPI, emailAPI } from "../../services/api"; // Ensure tasksAPI and emailAPI are imported
import {
  AttachFile,
  Clear,
  Download,
  InsertDriveFile,
  Delete,
} from "@mui/icons-material";

const TaskDialog = ({
  open,
  onClose,
  task,
  parentId,
  users,
  currentUser,
  onSave,
  onDelete,
}) => {
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (task?.attachments) {
      setAttachments(task.attachments);
    } else {
      setAttachments([]);
    }
    setFiles([]);
  }, [task]);

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Anhang wirklich löschen?")) return;
    try {
      await tasksAPI.deleteAttachment(task.id, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (e) {
      console.error("Failed to delete attachment", e);
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await tasksAPI.downloadAttachment(
        task.id,
        attachment.id,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error("Failed to download attachment", e);
    }
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [tenantsRes, propertiesRes, artisansRes] = await Promise.all([
            tenantAPI.getTenants(),
            propertyAPI.getProperties(),
            artisanAPI.getArtisans(),
          ]);
          setTenants(tenantsRes.data);
          setProperties(propertiesRes.data);
          setArtisans(artisansRes.data);

          // Build available emails: Check localStorage first, else fetch
          let availableEmails = [];
          const cachedEmails = localStorage.getItem("emails");
          if (cachedEmails) {
            try {
              availableEmails = JSON.parse(cachedEmails);
            } catch (e) {
              console.warn("Failed to parse local emails", e);
            }
          }

          if (availableEmails.length === 0) {
            try {
              const emailRes = await emailAPI.getEmailMessages({ limit: 100 });
              if (emailRes.data && Array.isArray(emailRes.data.items)) {
                availableEmails = emailRes.data.items;
              } else if (Array.isArray(emailRes.data)) {
                availableEmails = emailRes.data; // Fallback
              }
            } catch (e) {
              console.error("Failed to load email suggestions", e);
            }
          }
          setEmailSuggestions(availableEmails);
        } catch (error) {
          console.error("Failed to fetch references", error);
        }
      };
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setSaving(false);
    }
  }, [open]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    assignee_ids: [],
    mentioned_user_ids: [],
    parent_task_id: null,
    deadline: null,
    create_calendar_entry: false,
    tenant_id: null,
    property_id: null,
    artisan_id: null,
    email_ids: [],
  });

  useEffect(() => {
    if (open) {
      if (task) {
        setFormData({
          title: task.title || "",
          description: task.description || "",
          status: task.status || "todo",
          priority: task.priority || "medium",
          assignee_ids: task.assignees?.map((user) => user.id) || [],
          mentioned_user_ids:
            task.mentioned_users?.map((user) => user.id) || [],
          parent_task_id: task.parent_task_id || null,
          deadline: task.deadline ? task.deadline.split("T")[0] : null,
          create_calendar_entry: task.create_calendar_entry || false,
          tenant_id: task.tenant_id || null,
          property_id: task.property_id || null,
          artisan_id: task.artisan_id || null,
          email_ids: task.emails ? task.emails.map((e) => e.id) : [],
        });
      } else {
        setFormData({
          title: "",
          description: "",
          status: "todo",
          priority: "medium",
          assignee_ids: currentUser ? [currentUser.id] : [],
          mentioned_user_ids: [],
          parent_task_id: parentId || null,
          deadline: null,
          create_calendar_entry: false,
          tenant_id: null,
          property_id: null,
          artisan_id: null,
          email_ids: [],
        });
      }
    }
  }, [open, task, parentId, currentUser]);

  const handleSave = () => {
    setSaving(true);
    onSave(formData, files);
  };

  const handleDelete = () => {
    if (onDelete && task) {
      // Confirm logic can be here or in parent. The original had it inline.
      // Keeping it consistent with original "deletion inside dialog" behavior
      onDelete(task.id, task.parent_task_id);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {task && task.id
          ? task.parent_task_id
            ? "Unteraufgabe bearbeiten"
            : "Aufgabe bearbeiten"
          : parentId
            ? "Neue Unteraufgabe erstellen"
            : "Neue Aufgabe erstellen"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {task?.emails && task.emails.length > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Verknüpfte E-Mails:
              </Typography>
              {task.emails.map((email) => (
                <Box key={email.id} mb={0.5}>
                  <Link
                    component={RouterLink}
                    to={`/emails?id=${email.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {email.subject || "Kein Betreff"} (ID: {email.id})
                  </Link>
                </Box>
              ))}
            </Box>
          )}

          <TextField
            fullWidth
            label="Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                lang: "de",
                spellCheck: "true",
                autoCorrect: "on",
              },
            }}
          />

          <TextField
            fullWidth
            label="Beschreibung"
            multiline
            rows={10}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                lang: "de",
                spellCheck: "true",
                autoCorrect: "on",
              },
            }}
          />

          <TextField
            fullWidth
            label="Deadline"
            type="date"
            value={formData.deadline || ""}
            onChange={(e) =>
              setFormData({ ...formData, deadline: e.target.value })
            }
            sx={{ mb: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Tooltip title="Wenn aktiviert, wird automatisch ein Kalendereintrag für diese Aufgabe zur Deadline erstellt.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.create_calendar_entry}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      create_calendar_entry: e.target.checked,
                    })
                  }
                  disabled={!!task?.calendar_event_id}
                />
              }
              label={
                task?.calendar_event_id ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">
                      Kalendereintrag erstellt
                    </Typography>
                    <Link
                      component={RouterLink}
                      to={`/calendar?event=${task.calendar_event_id}`}
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      (Zum Kalendereintrag)
                    </Link>
                  </Box>
                ) : (
                  "Kalendereintrag erstellen"
                )
              }
              sx={{ mb: 2 }}
            />
          </Tooltip>

          <Grid container spacing={3}>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <MenuItem value="todo">ToDo</MenuItem>
                  <MenuItem value="in_progress">In Bearbeitung</MenuItem>
                  <MenuItem value="done">Erledigt</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priorität</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priorität"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                >
                  <MenuItem value="low">Niedrig</MenuItem>
                  <MenuItem value="medium">Mittel</MenuItem>
                  <MenuItem value="high">Hoch</MenuItem>
                  <MenuItem value="urgent">Dringend</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={tenants}
                getOptionLabel={(option) =>
                  `${option.name} (${option.tenant_number})`
                }
                value={tenants.find((t) => t.id === formData.tenant_id) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    tenant_id: newValue ? newValue.id : null,
                    property_id: newValue ? newValue.property_id : null,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Mieter" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={properties}
                getOptionLabel={(option) =>
                  `${option.address} (${option.property_number})`
                }
                value={
                  properties.find((p) => p.id === formData.property_id) || null
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    property_id: newValue ? newValue.id : null,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Immobilie" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={artisans}
                getOptionLabel={(option) => option.company_name}
                value={
                  artisans.find((a) => a.id === formData.artisan_id) || null
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    artisan_id: newValue ? newValue.id : null,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Handwerker" />
                )}
              />
            </Grid>

            {/* Email Association Autocomplete */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={emailSuggestions}
                getOptionLabel={(option) =>
                  `${option.subject || "(Kein Betreff)"} (${option.sender})`
                }
                value={
                  // Ensure we can display emails even if they are not in the suggestions list (e.g. from task.emails)
                  [...(task?.emails || []), ...emailSuggestions]
                    .filter(
                      (email, index, self) =>
                        index === self.findIndex((e) => e.id === email.id),
                    ) // Unique by ID
                    .filter((email) => formData.email_ids.includes(email.id))
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    email_ids: newValue.map((e) => e.id),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Verknüpfte E-Mails"
                    placeholder="E-Mails suchen..."
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option) => option.full_name || option.username}
                value={users.filter((user) =>
                  formData.assignee_ids.includes(user.id),
                )}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    assignee_ids: newValue.map((user) => user.id),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Zugewiesen an"
                    placeholder="Nutzer auswählen"
                    size="large"
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: "60px",
                        fontSize: "1.1rem",
                        paddingLeft: "20px",
                        paddingRight: "40px",
                        minWidth: "200px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "2px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiChip-root": {
                    fontSize: "1rem",
                    height: "36px",
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option) => option.full_name || option.username}
                value={users.filter((user) =>
                  formData.mentioned_user_ids.includes(user.id),
                )}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    mentioned_user_ids: newValue.map((user) => user.id),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nutzer erwähnen"
                    placeholder="Nutzer auswählen"
                    size="large"
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: "60px",
                        fontSize: "1.1rem",
                        paddingLeft: "20px",
                        paddingRight: "40px",
                        minWidth: "200px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "2px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiChip-root": {
                    fontSize: "1rem",
                    height: "36px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              Anhänge
            </Typography>
            <List dense>
              {attachments.map((att) => (
                <ListItem
                  key={att.id}
                  secondaryAction={
                    <>
                      <IconButton
                        edge="end"
                        aria-label="download"
                        onClick={() => downloadAttachment(att)}
                        sx={{ mr: 1 }}
                      >
                        <Download />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteAttachment(att.id)}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemIcon>
                    <InsertDriveFile />
                  </ListItemIcon>
                  <ListItemText
                    primary={att.filename}
                    secondary={new Date(att.created_at).toLocaleDateString()}
                  />
                </ListItem>
              ))}
              {files.map((file, index) => (
                <ListItem
                  key={`new-${index}`}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="remove"
                      onClick={() => removeFile(index)}
                    >
                      <Clear />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <AttachFile />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={`Neu (${(file.size / 1024).toFixed(1)} KB)`}
                  />
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFile />}
              sx={{ mt: 1 }}
            >
              Dateien auswählen
              <input type="file" hidden multiple onChange={handleFileChange} />
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {task && onDelete ? (
          <Button
            onClick={() => {
              if (
                window.confirm(
                  "Sind Sie sicher, dass Sie diese Aufgabe löschen möchten?",
                )
              ) {
                handleDelete();
                onClose();
              }
            }}
            color="error"
          >
            Löschen
          </Button>
        ) : (
          // Placeholder to keep alignment if needed, or simply nothing
          <Box />
        )}
        <Box sx={{ ml: "auto" }}>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.title.trim()}
            startIcon={
              saving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {task ? "Aktualisieren" : "Erstellen"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDialog;
